/**
 * CHUG CHAMP BEER LEADERBOARD - FIXED VERSION
 * Robust initialization without relying on custom elements
 */

(function() {
  'use strict';

  // Force initialization on multiple triggers
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  }

  window.addEventListener('load', init);

  // Try immediate init if DOM already loaded
  if (document.readyState !== 'loading') {
    setTimeout(init, 100);
  }

  function init() {
    const containers = document.querySelectorAll('.beer-lb');
    if (containers.length === 0) return;

    containers.forEach((container) => {
      if (container.hasAttribute('data-lb-init')) return;
      container.setAttribute('data-lb-init', '1');
      new BeerLeaderboard(container);
    });
  }

  // OCCS — Official Composite Chug Score (0–10)
  // Recalibrated speed so "fast" ~7–9, "world-class" 9–9.5, "record-breaking" 9.5–9.9.
  // Final score now 0..10 (previously 0..100). Only display rounds to 1 decimal.
  function clamp(x, a, b){ return Math.max(a, Math.min(b, x)); }

  function OCCS({
    volume_oz,
    time_s,
    foam_pct,
    splash_pct,
    time_to_rim_s,
    time_to_setdown_s,
    container_type // "cup" | "can" | "bottle"
  }) {
    // --- Speed (70 pts), recalibrated ---
    const OCPS = volume_oz / time_s;              // "Chug Rate" (oz/s)
    const OCPS_floor = 2.0;
    const OCPS_peak  = 9.7;                       // saturates near record-breaking
    const gamma      = 0.18;                      // gentler curve → meaningful mid/high scores
    const ocps_norm  = clamp((OCPS - OCPS_floor) / (OCPS_peak - OCPS_floor), 0, 1);
    const SS_70      = 70 * Math.pow(ocps_norm, gamma);

    // --- Difficulty (volume × container), unchanged ---
    const DV = Math.pow(volume_oz / 12, 0.08);    // mild volume nudge
    const CF = ({ cup: 0.98, can: 1.00, bottle: 1.03 })[container_type] ?? 1.00;
    const DC = clamp(DV * CF, 0.93, 1.10);
    const SS_70_DC = SS_70 * DC;

    // --- Cleanliness (30 pts), unchanged ---
    const foam_penalty    = (foam_pct   ?? 0) * 100;
    const splash_penalty  = (splash_pct ?? 0) * 150;
    const rim_penalty     = Math.max(0, (time_to_rim_s     ?? 0) - 0.5) * 20;
    const setdown_penalty = Math.max(0, (time_to_setdown_s ?? 0) - 0.5) * 20;
    const P_current = foam_penalty + splash_penalty + rim_penalty + setdown_penalty;
    const CP = Math.min(30, P_current / 6);       // 0..30
    const CL_30 = 30 - CP;

    // --- Final: convert 0..100 → 0..10 ---
    const total100 = clamp(SS_70_DC + CL_30, 0, 100);
    return total100 / 10; // numeric 0..10
  }

  // UI helper (display only): round to 2 decimals
  function formatOCCS10(val){ return val.toFixed(2); }

  /**
   * Get progress bar color based on OCCS score
   */
  function getOCCSColor(occs) {
    const percentage = (occs / 10) * 100;
    if (percentage < 30) {
      return '#E41D34'; // Red for low scores (0-3.0)
    } else if (percentage < 70) {
      return '#FFCD4D'; // Yellow for mid scores (3.0-7.0)
    } else {
      return '#4CAF50'; // Green for high scores (7.0-10.0)
    }
  }

  class BeerLeaderboard {
    constructor(container) {
      this.container = container;

      // Elements
      this.viewButtons = this.container.querySelectorAll('.beer-lb__view-btn:not(.beer-lb__category-btn)');
      this.categoryButtons = this.container.querySelectorAll('.beer-lb__category-btn');
      this.metricButtons = this.container.querySelectorAll('.beer-lb__metric-btn');
      this.sizeButtons = this.container.querySelectorAll('.beer-lb__size-btn');
      this.podiumContainer = this.container.querySelector('.beer-lb__podium');
      this.tableRows = this.container.querySelectorAll('.beer-lb__table-row');
      this.podiumCards = this.container.querySelectorAll('.beer-entry-card--podium');

      // State
      this.currentView = 'overview';
      this.currentMetric = 'time';
      this.currentSize = 'all';
      this.currentLeaderboardType = '21plus'; // Default to 21+ view
      this.allEntries = [];
      this.showcaseSize = 'all';   // Independent showcase size filter
      this.showcaseMetric = 'time'; // Independent showcase metric filter

      console.log('Found elements:', {
        viewButtons: this.viewButtons.length,
        categoryButtons: this.categoryButtons.length,
        metricButtons: this.metricButtons.length,
        sizeButtons: this.sizeButtons.length,
        podiumCards: this.podiumCards.length,
        tableRows: this.tableRows.length
      });

      // Initialize
      this.extractEntriesData();
      this.setupViewToggle();
      this.setupCategoryFilter();
      this.setupMetricToggle();
      this.setupSizeFilter();
      this.setupTooltip();
      this.setupShowcaseFilters();

      // Initial sort and render by OCCS (default metric)
      this.sortAndRenumberTable();
      this.updateOCCSDisplays();  // Must run before updatePodium so card buttons get WCS values
      this.updatePodium();
      this.updateShowcase();

      console.log('BeerLeaderboard initialized successfully!');
    }

    /**
     * Extract entry data from DOM
     */
    extractEntriesData() {
      this.allEntries = Array.from(this.tableRows).map(row => {
        const timeMs = parseFloat(row.dataset.timeMs) || 0;
        const volumeOz = parseFloat(row.dataset.volumeOz) || 0;
        const time_s = timeMs / 1000;
        const foamPct = parseFloat(row.dataset.foam) || 0;
        const splashPct = parseFloat(row.dataset.splashPct) || 0;

        // Compute OCCS from data attributes
        const occs = OCCS({
          volume_oz: volumeOz,
          time_s: time_s,
          foam_pct: foamPct,
          splash_pct: splashPct,
          time_to_rim_s: parseFloat(row.dataset.timeToRim) || 0,
          time_to_setdown_s: parseFloat(row.dataset.timeToSetdown) || 0,
          container_type: row.dataset.container || 'cup'
        });

        return {
          element: row,
          rank: parseInt(row.dataset.rank) || 0,
          timeMs: timeMs,
          ocps: parseFloat(row.dataset.ocps) || 0,
          nss: parseFloat(row.dataset.nss) || 0,
          occs: occs, // keep numeric 0..10 for sorting
          volumeOz: volumeOz,
          foamPct: foamPct,
          splashPct: splashPct,
          beverage: row.dataset.beverage || '',
          date: row.dataset.date || '',
          location: row.dataset.location || '',
          hasProof: row.dataset.hasProof === 'true',
          leaderboardType: row.dataset.leaderboardType || '21+'
        };
      });

      console.log(`Extracted ${this.allEntries.length} entries`);
    }

    /**
     * Update OCCS displays with computed values
     */
    updateOCCSDisplays() {
      // NOTE: Podium cards are updated by updatePodiumCards() with filtered/sorted top 3
      // Don't update them here as it would overwrite with unsorted this.allEntries values

      // Update table rows
      this.allEntries.forEach((entry) => {
        const display = entry.element.querySelector('[data-occs-display]');
        if (display) {
          display.textContent = formatOCCS10(entry.occs);
        }

        // Update progress bar if present (3-card view only)
        const progressBar = entry.element.querySelector('[data-occs-progress]');
        if (progressBar) {
          const percentage = Math.min((entry.occs / 10) * 100, 100);
          progressBar.style.display = 'block';
          progressBar.style.width = `${percentage}%`;
          progressBar.style.backgroundColor = getOCCSColor(entry.occs);
        }

        // Update button data-wcs attribute for modal display
        const proofBtn = entry.element.querySelector('.table-proof-btn');
        if (proofBtn) {
          proofBtn.dataset.wcs = entry.occs.toFixed(2);
        }
      });
    }

    /**
     * VIEW TOGGLE (Overview / Table)
     */
    setupViewToggle() {
      if (!this.viewButtons || this.viewButtons.length === 0) {
        console.warn('No view buttons found');
        return;
      }

      console.log(`Setting up ${this.viewButtons.length} view buttons`);

      this.viewButtons.forEach((btn, index) => {
        console.log(`View button ${index}:`, btn.dataset.view);

        // Click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const view = btn.dataset.view;
          console.log('View button clicked:', view);
          this.setView(view);
        });

        // Keyboard support
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const view = btn.dataset.view;
            console.log('View button keyboard activated:', view);
            this.setView(view);
          }
        });
      });

      console.log('View toggle setup complete');
    }

    setView(view) {
      this.currentView = view;

      // Update button states
      this.viewButtons.forEach(btn => {
        const isActive = btn.dataset.view === view;
        btn.classList.toggle('beer-lb__view-btn--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      // Show/hide podium based on view (use setProperty with important to override CSS !important)
      if (this.podiumContainer) {
        if (view === 'table') {
          this.podiumContainer.style.setProperty('display', 'none', 'important');
        } else {
          this.podiumContainer.style.removeProperty('display');
        }
      }
    }

    /**
     * GLOBAL CATEGORY FILTER (21+ / NA-Only / All)
     * Affects BOTH showcase AND table
     */
    setupCategoryFilter() {
      // Use new global category buttons
      const globalCategoryBtns = this.container.querySelectorAll('.global-category__btn');

      if (!globalCategoryBtns || globalCategoryBtns.length === 0) {
        // Fallback to old category buttons
        if (!this.categoryButtons || this.categoryButtons.length === 0) {
          console.warn('No category buttons found');
          return;
        }
      }

      const btns = globalCategoryBtns.length > 0 ? globalCategoryBtns : this.categoryButtons;
      console.log(`Setting up ${btns.length} global category buttons`);

      btns.forEach((btn, index) => {
        const category = btn.dataset.globalCategory || btn.dataset.category;
        console.log(`Global category button ${index}:`, category);

        // Click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const cat = btn.dataset.globalCategory || btn.dataset.category;
          console.log('Global category button clicked:', cat);
          this.setCategory(cat, btns);
        });

        // Keyboard support
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const cat = btn.dataset.globalCategory || btn.dataset.category;
            console.log('Global category button keyboard activated:', cat);
            this.setCategory(cat, btns);
          }
        });
      });

      console.log('Global category filter setup complete');
    }

    setCategory(category, btns) {
      this.currentLeaderboardType = category;

      // Update button states - use passed btns or find them
      const buttons = btns || this.container.querySelectorAll('.global-category__btn');
      buttons.forEach(btn => {
        const btnCat = btn.dataset.globalCategory || btn.dataset.category;
        const isActive = btnCat === category;
        btn.classList.toggle('global-category__btn--active', isActive);
        btn.classList.toggle('beer-lb__category-btn--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      // Re-render BOTH showcase AND table
      this.sortAndRenumberTable();
      this.updateOCCSDisplays();
      this.updatePodium();
      this.updateShowcase();
    }

    /**
     * METRIC TOGGLE
     */
    setupMetricToggle() {
      if (!this.metricButtons || this.metricButtons.length === 0) {
        console.warn('No metric buttons found');
        return;
      }

      console.log(`Setting up ${this.metricButtons.length} metric buttons`);

      this.metricButtons.forEach((btn, index) => {
        console.log(`Metric button ${index}:`, btn.dataset.metric);

        // Click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const metric = btn.dataset.metric;
          console.log('Metric button clicked:', metric);
          this.setMetric(metric);
        });

        // Keyboard support
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const metric = btn.dataset.metric;
            console.log('Metric button keyboard activated:', metric);
            this.setMetric(metric);
          }
        });
      });

      console.log('Metric toggle setup complete');
    }

    setMetric(metric) {
      const wasAlreadyOnThisMetric = this.currentMetric === metric;

      this.currentMetric = metric;
      this.container.setAttribute('data-emphasis', metric);

      this.metricButtons.forEach(btn => {
        const isActive = btn.dataset.metric === metric;
        btn.classList.toggle('beer-lb__metric-btn--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      // Mobile: Handle tooltip visibility for WCS
      if (this.tooltipHelpers && this.tooltipHelpers.isMobile()) {
        if (metric === 'occs') {
          if (wasAlreadyOnThisMetric) {
            // Clicking WCS again - toggle the tooltip
            if (this.tooltipHelpers.isTooltipVisible()) {
              this.tooltipHelpers.hideTooltip();
            } else {
              this.tooltipHelpers.showTooltip();
            }
          } else {
            // Switching to WCS - show tooltip
            this.tooltipHelpers.showTooltip();
          }
        } else {
          // Switching to Time - hide tooltip
          this.tooltipHelpers.hideTooltip();
        }
      }

      this.sortAndRenumberTable();
      this.updateOCCSDisplays();  // Must run before updatePodium so card buttons get WCS values
      this.updatePodium();
    }

    /**
     * SIZE FILTER
     */
    setupSizeFilter() {
      if (!this.sizeButtons || this.sizeButtons.length === 0) {
        console.warn('No size buttons found');
        return;
      }

      console.log(`Setting up ${this.sizeButtons.length} size buttons`);

      this.sizeButtons.forEach((btn, index) => {
        console.log(`Size button ${index}:`, btn.dataset.size);

        // Click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const size = btn.dataset.size;
          console.log('Size button clicked:', size);
          this.setSize(size);
        });

        // Keyboard support
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const size = btn.dataset.size;
            console.log('Size button keyboard activated:', size);
            this.setSize(size);
          }
        });
      });

      console.log('Size filter setup complete');
    }

    setSize(size) {
      this.currentSize = size;

      this.sizeButtons.forEach(btn => {
        const isActive = btn.dataset.size === size;
        btn.classList.toggle('beer-lb__size-btn--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      this.sortAndRenumberTable();
      this.updateOCCSDisplays();  // Must run before updatePodium so card buttons get WCS values
      this.updatePodium();
    }

    /**
     * TOOLTIP SETUP (Weighted Chug Score)
     */
    setupTooltip() {
      const tooltipButton = this.container.querySelector('[data-has-tooltip]');
      const tooltip = this.container.querySelector('.beer-lb__tooltip');

      if (!tooltipButton || !tooltip) {
        console.warn('Tooltip elements not found');
        return;
      }

      console.log('Setting up tooltip for Weighted Chug Score button');

      // Check if mobile
      const isMobile = () => window.innerWidth <= 749;

      // Helper functions
      const showTooltip = () => {
        tooltip.classList.add('visible');
        tooltip.setAttribute('aria-hidden', 'false');
      };

      const hideTooltip = () => {
        tooltip.classList.remove('visible');
        tooltip.setAttribute('aria-hidden', 'true');
      };

      const isTooltipVisible = () => tooltip.classList.contains('visible');

      // Store helpers on instance for use in setMetric
      this.tooltipHelpers = { showTooltip, hideTooltip, isTooltipVisible, isMobile };

      // Desktop: Show tooltip on mouse enter
      tooltipButton.addEventListener('mouseenter', () => {
        showTooltip();
      });

      // Desktop: Hide tooltip on mouse leave
      tooltipButton.addEventListener('mouseleave', () => {
        hideTooltip();
      });

      // CHANGE 4: Simplified mobile tooltip dismiss - one tap anywhere dismisses
      // Removed debounce flag for cleaner UX per user request
      document.addEventListener('click', (e) => {
        if (!isMobile()) return;
        if (!isTooltipVisible()) return;

        // If clicking the A.S.S. button, let setMetric handle the toggle
        if (tooltipButton.contains(e.target)) return;

        // Any other tap dismisses immediately - no debounce
        hideTooltip();
      });

      // Keyboard accessibility: Show tooltip on focus
      tooltipButton.addEventListener('focus', () => {
        showTooltip();
      });

      // Keyboard accessibility: Hide tooltip on blur
      tooltipButton.addEventListener('blur', () => {
        hideTooltip();
      });

      // Keyboard: Escape key to dismiss
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isTooltipVisible()) {
          hideTooltip();
          tooltipButton.focus();
        }
      });

      console.log('Tooltip setup complete');
    }

    /**
     * Sort table and renumber ranks (max 20 visible)
     */
    sortAndRenumberTable() {
      const tbody = this.container.querySelector('.beer-lb__table tbody');
      if (!tbody) return;

      const allRows = Array.from(this.tableRows);

      // Filter by size and leaderboard type
      const filtered = allRows.filter(row => {
        const vol = parseFloat(row.dataset.volumeOz);
        const lbType = row.dataset.leaderboardType || '21+';
        return this.shouldShowEntry(vol, lbType);
      });

      // Sort by current metric
      const sorted = this.rankEntries(
        filtered.map(row => {
          const timeMs = parseFloat(row.dataset.timeMs) || 0;
          const volumeOz = parseFloat(row.dataset.volumeOz) || 0;
          const time_s = timeMs / 1000;
          const foamPct = parseFloat(row.dataset.foam) || 0;
          const splashPct = parseFloat(row.dataset.splashPct) || 0;
          const occs = OCCS({
            volume_oz: volumeOz,
            time_s: time_s,
            foam_pct: foamPct,
            splash_pct: splashPct,
            time_to_rim_s: parseFloat(row.dataset.timeToRim) || 0,
            time_to_setdown_s: parseFloat(row.dataset.timeToSetdown) || 0,
            container_type: row.dataset.container || 'cup'
          });
          return {
            element: row,
            timeMs: timeMs,
            ocps: parseFloat(row.dataset.ocps) || 0,
            nss: parseFloat(row.dataset.nss) || 0,
            occs: occs, // keep numeric 0..10 for sorting
            foamPct: foamPct,
            splashPct: splashPct
          };
        }),
        this.currentMetric
      );

      // Limit to 20
      const top20 = sorted.slice(0, 20);

      // Hide all rows first
      allRows.forEach(row => {
        row.style.display = 'none';
        const detailsRow = row.nextElementSibling;
        if (detailsRow && detailsRow.hasAttribute('data-row-details')) {
          detailsRow.style.display = 'none';
        }
      });

      // Show and renumber top 20
      top20.forEach((item, index) => {
        const row = item.element;
        row.style.display = '';

        // Update rank cell
        const rankCell = row.querySelector('.table-cell__rank');
        if (rankCell) rankCell.textContent = index + 1;

        // Update OCCS display
        const occsDisplay = row.querySelector('[data-occs-display]');
        if (occsDisplay) occsDisplay.textContent = formatOCCS10(item.occs);

        // Update time display with two decimal points
        const timeCell = row.querySelector('.table-cell__time');
        if (timeCell) {
          const timeValue = (item.timeMs / 1000).toFixed(2);
          timeCell.innerHTML = `${timeValue}<span class="time-unit">S</span>`;
        }

        // Update chug rate display with two decimal points
        const ocpsCell = row.querySelector('.table-cell__ocps');
        if (ocpsCell) {
          const ocpsValue = item.ocps.toFixed(2);
          ocpsCell.textContent = `${ocpsValue} OZ/S`;
        }

        // Reorder in DOM
        tbody.appendChild(row);

        // Move details row if exists
        const detailsRow = row.nextElementSibling;
        if (detailsRow && detailsRow.hasAttribute('data-row-details')) {
          tbody.appendChild(detailsRow);
        }
      });
    }

    /**
     * Determine if entry should be shown based on size filter
     */
    shouldShowEntryBySize(volumeOz) {
      if (this.currentSize === 'all') return true;
      if (this.currentSize === '12') return volumeOz === 12;
      if (this.currentSize === '16') return volumeOz === 16;
      return true;
    }

    /**
     * Determine if entry should be shown based on leaderboard type filter
     */
    shouldShowEntryByLeaderboardType(leaderboardType) {
      if (this.currentLeaderboardType === 'all') return true;
      if (this.currentLeaderboardType === '21plus') return leaderboardType === '21+';
      if (this.currentLeaderboardType === 'na') return leaderboardType === 'NA';
      return true;
    }

    /**
     * Determine if entry should be shown (combined filters)
     */
    shouldShowEntry(volumeOz, leaderboardType) {
      return this.shouldShowEntryBySize(volumeOz) && this.shouldShowEntryByLeaderboardType(leaderboardType);
    }

    /**
     * UPDATE TOP-3 PODIUM (uses same filtered+sorted source as table)
     */
    updatePodium() {
      console.log('Updating podium with metric:', this.currentMetric, 'size:', this.currentSize, 'leaderboardType:', this.currentLeaderboardType);

      // Use exact same filter and sort pipeline as table
      const allRows = Array.from(this.tableRows);
      const filtered = allRows.filter(row => {
        const vol = parseFloat(row.dataset.volumeOz);
        const lbType = row.dataset.leaderboardType || '21+';
        return this.shouldShowEntry(vol, lbType);
      });

      const sorted = this.rankEntries(
        filtered.map(row => {
          const timeMs = parseFloat(row.dataset.timeMs) || 0;
          const volumeOz = parseFloat(row.dataset.volumeOz) || 0;
          const time_s = timeMs / 1000;
          const foamPct = parseFloat(row.dataset.foam) || 0;
          const splashPct = parseFloat(row.dataset.splashPct) || 0;
          const occs = OCCS({
            volume_oz: volumeOz,
            time_s: time_s,
            foam_pct: foamPct,
            splash_pct: splashPct,
            time_to_rim_s: parseFloat(row.dataset.timeToRim) || 0,
            time_to_setdown_s: parseFloat(row.dataset.timeToSetdown) || 0,
            container_type: row.dataset.container || 'cup'
          });
          return {
            element: row,
            timeMs: timeMs,
            ocps: parseFloat(row.dataset.ocps) || 0,
            nss: parseFloat(row.dataset.nss) || 0,
            occs: occs, // keep numeric 0..10 for sorting
            volumeOz: volumeOz,
            foamPct: foamPct,
            splashPct: splashPct
          };
        }),
        this.currentMetric
      );

      // Get top 3 for legacy podium cards
      const top3 = sorted.slice(0, 3);
      console.log('Top 3:', top3.map(e => ({ occs: e.occs, timeMs: e.timeMs, ocps: e.ocps })));

      // Hide podium if no matching entries (use setProperty with important to override CSS !important)
      if (top3.length === 0) {
        if (this.podiumContainer) {
          this.podiumContainer.style.setProperty('display', 'none', 'important');
        }
        return;
      }

      // Show podium if we have entries
      if (this.podiumContainer) {
        this.podiumContainer.style.removeProperty('display');
      }

      // Update legacy podium cards (if they exist)
      this.updatePodiumCards(top3);

      // Get top 5 for the new champs section
      const top5 = sorted.slice(0, 5);
      console.log('Top 5:', top5.map(e => ({ occs: e.occs, timeMs: e.timeMs, ocps: e.ocps })));

      // Update new champs section with A.S.S. scores
      this.updateChampsSection(top5);
    }

    /**
     * Rank entries by metric with full tiebreaker chain
     */
    rankEntries(entries, metric) {
      const sorted = [...entries];

      sorted.sort((a, b) => {
        let primary, tie2, tie3, tie4, tie5;

        if (metric === 'time') {
          // Time asc → ChugRate desc → OCCS desc → splash asc → foam asc
          primary = a.timeMs - b.timeMs;
          tie2 = b.ocps - a.ocps;
          tie3 = b.occs - a.occs;
          tie4 = (a.splashPct || 0) - (b.splashPct || 0);
          tie5 = (a.foamPct || 0) - (b.foamPct || 0);
        } else if (metric === 'occs') {
          // OCCS desc → time asc → ChugRate desc → splash asc → foam asc
          primary = b.occs - a.occs;
          tie2 = a.timeMs - b.timeMs;
          tie3 = b.ocps - a.ocps;
          tie4 = (a.splashPct || 0) - (b.splashPct || 0);
          tie5 = (a.foamPct || 0) - (b.foamPct || 0);
        }

        if (primary !== 0) return primary;
        if (tie2 !== 0) return tie2;
        if (tie3 !== 0) return tie3;
        if (tie4 !== 0) return tie4;
        return tie5;
      });

      return sorted;
    }

    /**
     * Update podium card visibility and content
     */
    updatePodiumCards(top3Entries) {
      console.log('Updating podium cards, showing:', top3Entries.length);

      // Hide all cards first (use setProperty with important to override CSS !important)
      this.podiumCards.forEach(card => {
        card.style.setProperty('display', 'none', 'important');
      });

      // Show and update top 3 cards
      top3Entries.forEach((entry, index) => {
        const card = this.podiumCards[index];
        if (card) {
          const row = entry.element;

          // Update card data attributes
          card.dataset.rank = entry.rank;
          card.dataset.timeMs = entry.timeMs;
          card.dataset.ocps = entry.ocps;
          card.dataset.nss = entry.nss;
          card.dataset.volumeOz = entry.volumeOz;

          // Update handle/name
          const nameContainer = card.querySelector('.card__name');
          if (nameContainer) {
            const handleCell = row.querySelector('.table-cell__handle');
            const handleLink = handleCell?.querySelector('.table-cell__link');

            if (handleLink) {
              nameContainer.innerHTML = `<a href="${handleLink.href}" class="card__name-link">${handleLink.textContent}</a>`;
            } else if (handleCell) {
              nameContainer.textContent = handleCell.textContent.trim();
            }
          }

          // Update hero box (Time & Chug Rate) - NEW COMPACT LAYOUT
          const heroTime = card.querySelector('.card__hero-time');
          if (heroTime) heroTime.innerHTML = `${(entry.timeMs / 1000).toFixed(2)}<span class="time-unit">S</span>`;

          const heroRate = card.querySelector('.card__hero-rate');
          if (heroRate) heroRate.textContent = `${entry.ocps.toFixed(2)} OZ/S`;

          // Update WCS score - NEW COMPACT LAYOUT
          const wcsScore = card.querySelector('.card__wcs-score');
          if (wcsScore) wcsScore.textContent = formatOCCS10(entry.occs);

          // Also update generic data-occs-display for compatibility
          const occsDisplay = card.querySelector('[data-occs-display]');
          if (occsDisplay) occsDisplay.textContent = formatOCCS10(entry.occs);

          // Update WCS mini progress bar - NEW COMPACT LAYOUT
          const progressBarMini = card.querySelector('.card__wcs-progress-mini-fill');
          if (progressBarMini) {
            const percentage = Math.min((entry.occs / 10) * 100, 100);
            const color = getOCCSColor(entry.occs);
            progressBarMini.style.width = `${percentage}%`;
            progressBarMini.style.backgroundColor = color;
          }

          // Also update generic data-occs-progress for compatibility
          const progressBar = card.querySelector('[data-occs-progress]');
          if (progressBar) {
            const percentage = Math.min((entry.occs / 10) * 100, 100);
            const color = getOCCSColor(entry.occs);
            progressBar.style.display = 'block';
            progressBar.style.width = `${percentage}%`;
            progressBar.style.backgroundColor = color;
          }

          // Update condensed info line - NEW COMPACT LAYOUT
          const volumeSpan = card.querySelector('.card__info-volume');
          if (volumeSpan) volumeSpan.textContent = `${entry.volumeOz}oz`;

          const beverageSpan = card.querySelector('.card__info-beverage');
          if (beverageSpan) {
            beverageSpan.textContent = row.dataset.beverage || 'Beer (Cup)';
          }

          const dateSpan = card.querySelector('.card__info-date');
          if (dateSpan) {
            const location = row.dataset.location || '';
            dateSpan.textContent = location;
          }

          // Update replay icon - copy all data attributes for Stats Dock modal
          const replayIcon = card.querySelector('.card__replay-icon');
          const proofLink = row.querySelector('.table-proof-btn');
          if (replayIcon && !replayIcon.classList.contains('card__replay-icon--empty')) {
            if (proofLink && proofLink.dataset.videoUrl) {
              // Copy all video/stats data attributes from table button to card button
              replayIcon.dataset.videoUrl = proofLink.dataset.videoUrl;
              replayIcon.dataset.entryName = proofLink.dataset.entryName || '';
              replayIcon.dataset.entryTime = proofLink.dataset.entryTime || '';
              replayIcon.dataset.wcs = proofLink.dataset.wcs || '';
              replayIcon.dataset.volumeOz = proofLink.dataset.volumeOz || '';
              replayIcon.dataset.chugRate = proofLink.dataset.chugRate || '';
              replayIcon.dataset.beverage = proofLink.dataset.beverage || '';
              replayIcon.dataset.date = proofLink.dataset.date || '';
              replayIcon.dataset.location = proofLink.dataset.location || '';
              replayIcon.dataset.proofUrl = proofLink.dataset.proofUrl || '';
              replayIcon.style.display = '';
            } else {
              replayIcon.style.display = 'none';
            }
          }

          // Update card social icon based on table row's proof URL (Instagram/TikTok posts)
          const cardSocialIcon = card.querySelector('.card__social-icon');
          const tableSocialIcon = row.querySelector('.table__social-icon');
          const proofUrl = tableSocialIcon ? tableSocialIcon.dataset.proofUrl : null;
          const proofUrlLower = proofUrl ? proofUrl.toLowerCase() : '';
          const isSocialProof = proofUrlLower && (proofUrlLower.includes('instagram') || proofUrlLower.includes('tiktok'));

          if (cardSocialIcon) {
            if (isSocialProof) {
              // Update card social icon with table's proof URL
              cardSocialIcon.href = proofUrl;
              cardSocialIcon.dataset.proofUrl = proofUrl;
              // Update icon type based on URL (TikTok vs Instagram)
              const isTiktok = proofUrlLower.includes('tiktok');
              const svgHtml = isTiktok
                ? '<svg class="card__social-svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.88 2.89 2.89 0 0 1-2.88-2.88 2.89 2.89 0 0 1 2.88-2.88c.28 0 .55.04.81.11v-3.5a6.37 6.37 0 0 0-.81-.05A6.34 6.34 0 0 0 3.15 15.6 6.34 6.34 0 0 0 9.49 22a6.34 6.34 0 0 0 6.34-6.34V9.41a8.16 8.16 0 0 0 4.76 1.53v-3.5a4.82 4.82 0 0 1-1-.75z"/></svg>'
                : '<svg class="card__social-svg" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2"/><circle cx="18" cy="6" r="1.5" fill="currentColor"/></svg>';
              cardSocialIcon.innerHTML = svgHtml;
              cardSocialIcon.style.display = '';
            } else {
              cardSocialIcon.style.display = 'none';
            }
          } else if (isSocialProof) {
            // Create card social icon if it doesn't exist but table has one
            const actionsContainer = card.querySelector('.card__actions');
            if (actionsContainer) {
              const isTiktok = proofUrlLower.includes('tiktok');
              const newSocialIcon = document.createElement('a');
              newSocialIcon.className = 'card__social-icon';
              newSocialIcon.href = proofUrl;
              newSocialIcon.dataset.proofUrl = proofUrl;
              newSocialIcon.target = '_blank';
              newSocialIcon.rel = 'noopener';
              newSocialIcon.setAttribute('aria-label', 'View on social media');
              newSocialIcon.innerHTML = isTiktok
                ? '<svg class="card__social-svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.88 2.89 2.89 0 0 1-2.88-2.88 2.89 2.89 0 0 1 2.88-2.88c.28 0 .55.04.81.11v-3.5a6.37 6.37 0 0 0-.81-.05A6.34 6.34 0 0 0 3.15 15.6 6.34 6.34 0 0 0 9.49 22a6.34 6.34 0 0 0 6.34-6.34V9.41a8.16 8.16 0 0 0 4.76 1.53v-3.5a4.82 4.82 0 0 1-1-.75z"/></svg>'
                : '<svg class="card__social-svg" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2"/><circle cx="18" cy="6" r="1.5" fill="currentColor"/></svg>';
              actionsContainer.insertBefore(newSocialIcon, actionsContainer.firstChild);
            }
          }

          // Legacy: Update Video Proof button (if still present)
          const proofBtn = card.querySelector('.card__proof-btn');
          if (proofBtn) {
            if (proofLink && proofLink.dataset.videoUrl) {
              proofBtn.dataset.videoUrl = proofLink.dataset.videoUrl;
              proofBtn.dataset.entryName = proofLink.dataset.entryName || '';
              proofBtn.dataset.entryTime = proofLink.dataset.entryTime || '';
              proofBtn.dataset.wcs = proofLink.dataset.wcs || '';
              proofBtn.dataset.volumeOz = proofLink.dataset.volumeOz || '';
              proofBtn.dataset.chugRate = proofLink.dataset.chugRate || '';
              proofBtn.dataset.beverage = proofLink.dataset.beverage || '';
              proofBtn.dataset.date = proofLink.dataset.date || '';
              proofBtn.dataset.location = proofLink.dataset.location || '';
              proofBtn.style.display = '';
            } else {
              proofBtn.style.display = 'none';
            }
          }

          // Update card class
          card.className = `beer-entry-card beer-entry-card--podium beer-entry-card--rank-${index + 1}`;

          // Show card (remove the display:none !important we set when hiding)
          card.style.removeProperty('display');
          console.log(`Showing card ${index + 1} for entry with OCCS ${entry.occs}`);
        }
      });
    }

    /**
     * Update THE CHUG CHAMPS section with top 5 entries
     * This populates the A.S.S. scores in the new vertical list layout
     */
    updateChampsSection(top5Entries) {
      const champsRows = this.container.querySelectorAll('.champs__row');
      if (!champsRows || champsRows.length === 0) {
        console.log('No champs rows found - using legacy podium layout');
        return;
      }

      console.log('Updating champs section with top 5 entries');

      // Update each champs row with A.S.S. score
      champsRows.forEach((row, index) => {
        if (index < top5Entries.length) {
          const entry = top5Entries[index];

          // Update A.S.S. score display
          const bsDisplay = row.querySelector('[data-occs-display]');
          if (bsDisplay) {
            bsDisplay.textContent = formatOCCS10(entry.occs);
          }

          // Update handle if needed (in case filtering changes top 5)
          const handleSpan = row.querySelector('.champs__handle');
          const handleCell = entry.element.querySelector('.table-cell__handle');
          if (handleSpan && handleCell) {
            const handleText = handleCell.textContent.trim();
            handleSpan.textContent = `@${handleText.toUpperCase()}`;
          }

          // Update time
          const timeSpan = row.querySelector('.champs__time');
          if (timeSpan) {
            timeSpan.textContent = `${(entry.timeMs / 1000).toFixed(2)}s`;
          }

          // Show the row
          row.style.display = '';
        } else {
          // Hide extra rows if fewer than 5 entries
          row.style.display = 'none';
        }
      });
    }

    /**
     * SHOWCASE FILTERS (independent from Command Center, but respects global category)
     */
    setupShowcaseFilters() {
      const showcaseFilterBtns = this.container.querySelectorAll('.showcase__filter-btn');
      if (!showcaseFilterBtns || showcaseFilterBtns.length === 0) {
        console.log('No showcase filter buttons found');
        return;
      }

      // Initialize showcase filter state
      this.showcaseSize = 'all';
      this.showcaseMetric = 'time';

      console.log(`Setting up ${showcaseFilterBtns.length} showcase filter buttons`);

      showcaseFilterBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const size = btn.dataset.showcaseSize;
          const metric = btn.dataset.showcaseMetric;

          // Update active state within group
          const group = btn.closest('.showcase__filter-group');
          group.querySelectorAll('.showcase__filter-btn').forEach(b =>
            b.classList.remove('showcase__filter-btn--active')
          );
          btn.classList.add('showcase__filter-btn--active');

          // Update filter state
          if (size) {
            this.showcaseSize = size;
            console.log('Showcase size changed to:', size);
          }
          if (metric) {
            this.showcaseMetric = metric;
            console.log('Showcase metric changed to:', metric);
          }

          // Re-render showcase only (not table)
          this.updateShowcase();
        });
      });

      console.log('Showcase filters setup complete');
    }

    /**
     * Update showcase entries based on filters (0-5 entries, no duplicates)
     */
    updateShowcase() {
      const showcaseEntries = this.container.querySelectorAll('.showcase__entry');
      const emptyState = this.container.querySelector('.showcase__empty');

      if (!showcaseEntries || showcaseEntries.length === 0) {
        console.log('No showcase entries found - skipping showcase update');
        return;
      }

      console.log('Updating showcase with size:', this.showcaseSize, 'metric:', this.showcaseMetric);

      const allRows = Array.from(this.tableRows);

      // Filter by GLOBAL category + showcase size
      const filtered = allRows.filter(row => {
        // Respect global category filter
        const lbType = row.dataset.leaderboardType || '21+';
        if (!this.shouldShowEntryByLeaderboardType(lbType)) return false;

        // Apply showcase size filter
        const vol = parseFloat(row.dataset.volumeOz);
        if (this.showcaseSize === 'all') return true;
        if (this.showcaseSize === '12') return vol === 12;
        if (this.showcaseSize === '16') return vol === 16;
        return true;
      });

      // Sort by showcase metric
      const sorted = this.rankEntries(
        filtered.map(row => {
          const timeMs = parseFloat(row.dataset.timeMs) || 0;
          const volumeOz = parseFloat(row.dataset.volumeOz) || 0;
          const occs = OCCS({
            volume_oz: volumeOz,
            time_s: timeMs / 1000,
            foam_pct: parseFloat(row.dataset.foam) || 0,
            splash_pct: parseFloat(row.dataset.splashPct) || 0,
            time_to_rim_s: parseFloat(row.dataset.timeToRim) || 0,
            time_to_setdown_s: parseFloat(row.dataset.timeToSetdown) || 0,
            container_type: row.dataset.container || 'cup'
          });
          return { element: row, timeMs, occs, ocps: parseFloat(row.dataset.ocps) || 0 };
        }),
        this.showcaseMetric === 'ass' ? 'occs' : 'time'
      );

      // Get top 5 (max) - no duplicates possible since filtering from unique table rows
      const top5 = sorted.slice(0, 5);

      // Handle empty state
      if (top5.length === 0) {
        if (emptyState) emptyState.style.display = '';
        // Use setProperty to override CSS !important
        showcaseEntries.forEach(entry => entry.style.setProperty('display', 'none', 'important'));
        return;
      }

      // Hide empty state
      if (emptyState) emptyState.style.display = 'none';

      // Update showcase entries (show only what exists)
      showcaseEntries.forEach((entry, index) => {
        if (index < top5.length) {
          const data = top5[index];
          const row = data.element;

          // Update rank number
          const rankEl = entry.querySelector('.showcase__rank');
          if (rankEl) rankEl.textContent = index + 1;

          // Update handle
          const handleEl = entry.querySelector('.showcase__handle');
          const handleCell = row.querySelector('.table-cell__handle');
          if (handleEl && handleCell) {
            const handleLink = handleCell.querySelector('.table-cell__link');
            if (handleLink) {
              handleEl.href = handleLink.href;
              handleEl.textContent = `@${handleLink.textContent.trim().toUpperCase()}`;
            } else {
              handleEl.textContent = `@${handleCell.textContent.trim().toUpperCase()}`;
            }
          }

          // Update metric value (time or A.S.S.)
          const metricEl = entry.querySelector('.showcase__metric-value');
          if (metricEl) {
            if (this.showcaseMetric === 'ass') {
              metricEl.textContent = formatOCCS10(data.occs);
            } else {
              metricEl.textContent = `${(data.timeMs / 1000).toFixed(2)}s`;
            }
          }

          // Update play button data from table row
          const playBtn = entry.querySelector('.showcase__play-btn');
          const proofBtn = row.querySelector('.table-proof-btn');
          if (playBtn && proofBtn) {
            playBtn.dataset.videoUrl = proofBtn.dataset.videoUrl || '';
            playBtn.dataset.entryName = proofBtn.dataset.entryName || '';
            playBtn.dataset.entryTime = proofBtn.dataset.entryTime || '';
            playBtn.dataset.wcs = formatOCCS10(data.occs);
            playBtn.dataset.volumeOz = proofBtn.dataset.volumeOz || '';
            playBtn.dataset.chugRate = proofBtn.dataset.chugRate || '';
            playBtn.dataset.beverage = proofBtn.dataset.beverage || '';
            playBtn.dataset.date = proofBtn.dataset.date || '';
            playBtn.dataset.location = proofBtn.dataset.location || '';
            playBtn.dataset.proofUrl = proofBtn.dataset.proofUrl || '';
          }

          entry.style.setProperty('display', 'grid', 'important');
        } else {
          // Hide extra slots if fewer than 5 entries
          entry.style.setProperty('display', 'none', 'important');
        }
      });

      console.log(`Showcase updated with ${Math.min(top5.length, 5)} entries`);
    }

  }

})();
